import { useQuery } from '@tanstack/react-query';

import { useUniversalNavContext } from '../../universal-nav/hooks/useUniversalNavContext';

import type { ZapierProfile } from '../../universal-nav/types';

type AccountsResponseRaw = {
  results: Array<{
    id: number;
    logo_url: string;
    name: string;
    owner: {
      id: number;
      name: string;
    };
    plan_info: {
      allow_teams: boolean;
      name: string;
    };
  }>;
};

type ProfilesResponseRaw = {
  objects: Array<{
    id: number;
    photo_url: string;
  }>;
};

export const prepareAccountsListData = (
  accountsJson: AccountsResponseRaw,
  profilesJson: ProfilesResponseRaw
): Array<ZapierProfile> => {
  const accounts = accountsJson.results;
  const profiles = profilesJson.objects;

  const output = accounts.map((account) => {
    let avatar = account.logo_url;
    let name = account.name;

    // Determine if this account is a organization or person
    if (account.plan_info.allow_teams === false) {
      name = account.owner.name;

      // If it's a person, we need to search the list of profiles
      // for the matching profile because the profile is the only
      // place to find their avatar url.
      const profile = profiles.find((p) => p.id === account.owner.id);
      if (profile) avatar = profile.photo_url;
    }

    return {
      id: account.id,
      normalizedImageUrl: avatar,
      normalizedName: name,
      planName: account.plan_info.name,
    };
  });

  const hasUndefinedValues =
    Object.values(output).filter((x) => x === undefined).length > 0;
  if (hasUndefinedValues) throw Error('Invalid input JSON');

  return output;
};

async function fetchData(apiHostname: string): Promise<Array<ZapierProfile>> {
  const accountsResponse = await fetch(
    `${apiHostname}/api/v4/accounts?limit=9999`,
    {
      credentials: process.env.NODE_ENV !== 'test' ? 'include' : 'same-origin',
    }
  );

  const profilesResponse = await fetch(`${apiHostname}/api/v3/profiles`, {
    credentials: process.env.NODE_ENV !== 'test' ? 'include' : 'same-origin',
  });

  if (!accountsResponse.ok || !profilesResponse.ok) {
    throw new Error('Network response was not ok');
  }

  const accountsJson = await accountsResponse.json();
  const profilesJson = await profilesResponse.json();

  return prepareAccountsListData(accountsJson, profilesJson);
}

export const useQueryAccountsList = () => {
  const { accountsListData: accountsListDataFromProps, apiHostname } =
    useUniversalNavContext();

  const result = useQuery<Array<ZapierProfile>>({
    queryKey: ['accounts'],
    queryFn: () => fetchData(apiHostname),
    enabled: !accountsListDataFromProps,
    initialData: accountsListDataFromProps,
  });

  return {
    ...result,
    // https://tanstack.com/query/v4/docs/react/guides/disabling-queries#isinitialloading
    isLoading: result.isInitialLoading,
  };
};
